// Month 6: Capstone Project (Days 101-120)
export const month6 = [
  {
    day: 101,
    month: 6,
    week: 21,
    monthTitle: "Capstone Project",
    weekTitle: "Project Ideation & Architecture",
    topic: "Project Ideation & Problem Selection",
    handsOn: ["Brainstorm project ideas", "Market research", "Problem validation", "Feasibility analysis"],
    example: "AI-powered application concept",
    codingTask: "Define your capstone project: Problem statement, Target users, Core features (MVP), Technical requirements, Success metrics.",
    assignment: "Create project proposal document: Executive summary, Market analysis, Technical architecture overview, Timeline with milestones, Risk assessment.",
    explanation: "Strong projects solve real problems. Proper planning prevents scope creep and ensures successful completion.",
    expectedInputs: "Project ideas and research",
    expectedOutputs: "Detailed project proposal",
    evaluationChecklist: ["Problem clearly defined", "Solution feasible in timeframe", "Technical requirements identified", "Proposal comprehensive"],
    gitTask: "Create capstone-project repo, Commit proposal document",
    resourceLinks: [
      { title: "Project Planning Guide", url: "https://youtu.be/TQZr3-mrsa8?si=pQzVbXrYtN9pWKAb" }
    ],
    tasks: [
      { id: "d101_resources", label: "Review learning resources" },
      { id: "d101_handson", label: "Complete hands-on: Brainstorm and validate ideas" },
      { id: "d101_coding", label: "Coding task: Define project scope" },
      { id: "d101_assignment", label: "Assignment: Project proposal document" },
      { id: "d101_git", label: "Git: Create repo, commit proposal" }
    ]
  },
  {
    day: 102,
    month: 6,
    week: 21,
    monthTitle: "Capstone Project",
    weekTitle: "Project Ideation & Architecture",
    topic: "System Architecture Design",
    handsOn: ["Draw system architecture diagram", "Design database schema", "Define API endpoints", "Plan component interactions"],
    example: "Complete system blueprint",
    codingTask: "Create technical architecture: System architecture diagram, Database schema (all collections/tables), API endpoint specifications, Tech stack justification, Data flow diagrams.",
    assignment: "Document architecture decisions: Why this database?, Why this framework?, Scalability considerations, Security measures, Alternative approaches considered.",
    explanation: "Good architecture prevents technical debt and makes development smoother. Design before coding.",
    expectedInputs: "Project requirements",
    expectedOutputs: "Complete technical architecture",
    evaluationChecklist: ["Architecture diagram clear", "Database schema complete", "API endpoints defined", "Decisions justified"],
    gitTask: "Commit architecture documentation with diagrams",
    resourceLinks: [
      { title: "System Design Fundamentals", url: "https://youtu.be/i53Gi_K3o7I?si=mQzVbXrYtN9pWKAb" }
    ],
    tasks: [
      { id: "d102_resources", label: "Review learning resources" },
      { id: "d102_handson", label: "Complete hands-on: Architecture design" },
      { id: "d102_coding", label: "Coding task: Technical architecture document" },
      { id: "d102_assignment", label: "Assignment: Architecture decisions document" },
      { id: "d102_git", label: "Git: Commit architecture docs" }
    ]
  },
  {
    day: 103,
    month: 6,
    week: 21,
    monthTitle: "Capstone Project",
    weekTitle: "Project Ideation & Architecture",
    topic: "Data Collection & Preparation Strategy",
    handsOn: ["Identify data sources", "Plan data collection", "Design data pipeline", "Set up data storage"],
    example: "Data acquisition plan",
    codingTask: "Create data strategy: Data sources identified, Collection methods (APIs, scraping, datasets), Storage solution chosen, Processing pipeline designed, Sample data collected and validated.",
    assignment: "Build data collection scripts: Automated data gathering, Data validation, Error handling, Initial dataset of 100+ samples.",
    explanation: "AI projects live or die by data quality. Plan data collection thoroughly before building models.",
    expectedInputs: "Data requirements",
    expectedOutputs: "Data collection pipeline and initial dataset",
    evaluationChecklist: ["Data sources identified", "Collection automated", "Initial dataset collected", "Data quality validated"],
    gitTask: "Commit data collection scripts and sample data",
    resourceLinks: [
      { title: "Data Pipeline Design", url: "https://youtu.be/dvviIUKwH7o?si=mQzVbXrYtN9pWKAb" }
    ],
    tasks: [
      { id: "d103_resources", label: "Review learning resources" },
      { id: "d103_handson", label: "Complete hands-on: Data pipeline planning" },
      { id: "d103_coding", label: "Coding task: Data strategy document" },
      { id: "d103_assignment", label: "Assignment: Data collection scripts + 100 samples" },
      { id: "d103_git", label: "Git: Commit data pipeline" }
    ]
  },
  {
    day: 104,
    month: 6,
    week: 21,
    monthTitle: "Capstone Project",
    weekTitle: "Project Ideation & Architecture",
    topic: "Development Environment & Project Setup",
    handsOn: ["Set up development environment", "Configure version control", "Initialize project structure", "Set up CI/CD basics"],
    example: "Professional project setup",
    codingTask: "Initialize project infrastructure: Organized folder structure, Virtual environments, Requirements files, .gitignore configured, README template, Environment variables setup.",
    assignment: "Set up development workflow: Git branching strategy, Pre-commit hooks, Code formatting (black, prettier), Linting setup, Testing framework.",
    explanation: "Professional project setup ensures smooth collaboration and maintains code quality throughout development.",
    expectedInputs: "Project requirements",
    expectedOutputs: "Production-ready project structure",
    evaluationChecklist: ["Folder structure logical", "All tools configured", "Git workflow established", "Documentation started"],
    gitTask: "Commit project setup and configuration",
    resourceLinks: [
      { title: "Project Setup Best Practices", url: "https://youtu.be/Jnp-4_rMBVk?si=pQzVbXrYtN9pWKAb" }
    ],
    tasks: [
      { id: "d104_resources", label: "Review learning resources" },
      { id: "d104_handson", label: "Complete hands-on: Environment setup" },
      { id: "d104_coding", label: "Coding task: Project infrastructure" },
      { id: "d104_assignment", label: "Assignment: Development workflow setup" },
      { id: "d104_git", label: "Git: Commit project setup" }
    ]
  },
  {
    day: 105,
    month: 6,
    week: 21,
    monthTitle: "Capstone Project",
    weekTitle: "Project Ideation & Architecture",
    topic: "Backend Foundation & Database Setup",
    handsOn: ["Set up FastAPI project", "Configure database", "Create models", "Implement basic CRUD"],
    example: "Backend skeleton",
    codingTask: "Build backend foundation: FastAPI application structure, Database connection configured, Core models defined, Health check endpoint, Basic CRUD endpoints, Error handling middleware.",
    assignment: "Add authentication scaffold: User model, Registration endpoint, Login endpoint (JWT ready), Password hashing, Protected route example.",
    explanation: "Solid backend foundation enables rapid feature development. Get core infrastructure right first.",
    expectedInputs: "Architecture design",
    expectedOutputs: "Working backend skeleton",
    evaluationChecklist: ["FastAPI running", "Database connected", "Models created", "Basic endpoints work"],
    gitTask: "Commit backend foundation",
    resourceLinks: [],
    tasks: [
      { id: "d105_handson", label: "Complete hands-on: Backend setup" },
      { id: "d105_coding", label: "Coding task: Backend foundation" },
      { id: "d105_assignment", label: "Assignment: Authentication scaffold" },
      { id: "d105_git", label: "Git: Commit backend foundation" }
    ]
  },
  {
    day: 106,
    month: 6,
    week: 22,
    monthTitle: "Capstone Project",
    weekTitle: "Core Backend Development",
    topic: "Core Business Logic Implementation",
    handsOn: ["Implement main features", "Write business logic", "Add validation", "Test endpoints"],
    example: "Core functionality",
    codingTask: "Implement main backend features: 3-5 core API endpoints, Business logic layer, Input validation, Response formatting, Database operations.",
    assignment: "Write API tests: Unit tests for business logic, Integration tests for endpoints, Test edge cases, Achieve 70%+ code coverage.",
    explanation: "This is the heart of your application. Focus on correctness and clean code.",
    expectedInputs: "Feature requirements",
    expectedOutputs: "Working core features",
    evaluationChecklist: ["Core features functional", "Validation thorough", "Tests passing", "Code clean"],
    gitTask: "Commit core backend features",
    resourceLinks: [],
    tasks: [
      { id: "d106_handson", label: "Complete hands-on: Business logic implementation" },
      { id: "d106_coding", label: "Coding task: Core API endpoints" },
      { id: "d106_assignment", label: "Assignment: API test suite" },
      { id: "d106_git", label: "Git: Commit core features" }
    ]
  },
  {
    day: 107,
    month: 6,
    week: 22,
    monthTitle: "Capstone Project",
    weekTitle: "Core Backend Development",
    topic: "AI/ML Model Integration",
    handsOn: ["Train or load AI model", "Create prediction endpoint", "Handle model errors", "Optimize inference"],
    example: "AI-powered backend",
    codingTask: "Integrate AI capabilities: Train/finetune your model OR Load pre-trained model, Create prediction endpoint, Add input preprocessing, Handle model errors gracefully, Implement response formatting.",
    assignment: "Optimize AI integration: Add model caching, Implement batch prediction, Add fallback logic, Measure inference latency, Document model details.",
    explanation: "This is what makes your project 'AI-powered'. Ensure model integration is robust and performant.",
    expectedInputs: "User data for AI processing",
    expectedOutputs: "AI predictions via API",
    evaluationChecklist: ["Model integrated", "Predictions accurate", "Error handling robust", "Performance acceptable"],
    gitTask: "Commit AI model integration",
    resourceLinks: [],
    tasks: [
      { id: "d107_handson", label: "Complete hands-on: Model training/loading" },
      { id: "d107_coding", label: "Coding task: AI prediction endpoint" },
      { id: "d107_assignment", label: "Assignment: Optimization and documentation" },
      { id: "d107_git", label: "Git: Commit AI integration" }
    ]
  },
  {
    day: 108,
    month: 6,
    week: 22,
    monthTitle: "Capstone Project",
    weekTitle: "Core Backend Development",
    topic: "RAG System Implementation (if applicable)",
    handsOn: ["Set up vector database", "Implement document processing", "Build retrieval pipeline", "Integrate with LLM"],
    example: "RAG-powered features",
    codingTask: "Build RAG system (if your project uses RAG): Document upload and processing, Vector database indexing, Retrieval endpoint, LLM integration, Context-aware responses.",
    assignment: "Optimize RAG performance: Add query optimization, Implement caching, Fine-tune retrieval, Add evaluation metrics, Document RAG pipeline.",
    explanation: "RAG systems require careful tuning. Ensure retrieval quality and response accuracy.",
    expectedInputs: "Documents and queries",
    expectedOutputs: "Contextual AI responses",
    evaluationChecklist: ["Documents processed correctly", "Retrieval returns relevant results", "Responses use context", "Performance optimized"],
    gitTask: "Commit RAG implementation",
    resourceLinks: [],
    tasks: [
      { id: "d108_handson", label: "Complete hands-on: RAG pipeline setup" },
      { id: "d108_coding", label: "Coding task: Complete RAG system" },
      { id: "d108_assignment", label: "Assignment: RAG optimization and evaluation" },
      { id: "d108_git", label: "Git: Commit RAG system" }
    ]
  },
  {
    day: 109,
    month: 6,
    week: 22,
    monthTitle: "Capstone Project",
    weekTitle: "Core Backend Development",
    topic: "Agent/Automation Features (if applicable)",
    handsOn: ["Build agent system", "Implement tool use", "Add workflow automation", "Test agent reliability"],
    example: "Autonomous features",
    codingTask: "Implement agent capabilities (if applicable): ReAct agent or workflow, Tool integration, Multi-step task execution, Error recovery, Logging and monitoring.",
    assignment: "Test agent reliability: Run 50+ test tasks, Document success rate, Identify failure patterns, Implement improvements, Create usage examples.",
    explanation: "Agents add powerful automation but need thorough testing. Ensure reliability before deployment.",
    expectedInputs: "Complex tasks",
    expectedOutputs: "Autonomous task completion",
    evaluationChecklist: ["Agent completes tasks", "Tools used correctly", "Failures handled gracefully", "Success rate high"],
    gitTask: "Commit agent implementation",
    resourceLinks: [],
    tasks: [
      { id: "d109_handson", label: "Complete hands-on: Agent development" },
      { id: "d109_coding", label: "Coding task: Agent system" },
      { id: "d109_assignment", label: "Assignment: Reliability testing" },
      { id: "d109_git", label: "Git: Commit agent features" }
    ]
  },
  {
    day: 110,
    month: 6,
    week: 22,
    monthTitle: "Capstone Project",
    weekTitle: "Core Backend Development",
    topic: "Database Optimization & Advanced Features",
    handsOn: ["Add database indexes", "Optimize queries", "Implement caching", "Add advanced features"],
    example: "Performance-optimized backend",
    codingTask: "Optimize and enhance backend: Add database indexes for common queries, Implement Redis caching, Optimize slow endpoints, Add rate limiting, Implement request logging.",
    assignment: "Add advanced features: File upload/download, Email notifications, Scheduled tasks (if needed), Admin dashboard endpoints, Export functionality.",
    explanation: "Polish your backend with performance optimizations and user-requested features.",
    expectedInputs: "High traffic simulation",
    expectedOutputs: "Fast, feature-rich backend",
    evaluationChecklist: ["Queries optimized", "Caching working", "Advanced features functional", "Performance improved"],
    gitTask: "Commit optimizations and advanced features",
    resourceLinks: [],
    tasks: [
      { id: "d110_handson", label: "Complete hands-on: Performance optimization" },
      { id: "d110_coding", label: "Coding task: Database and caching optimization" },
      { id: "d110_assignment", label: "Assignment: Advanced features" },
      { id: "d110_git", label: "Git: Commit optimizations" }
    ]
  },
  {
    day: 111,
    month: 6,
    week: 23,
    monthTitle: "Capstone Project",
    weekTitle: "Frontend & Full Stack",
    topic: "Frontend Foundation & UI Design",
    handsOn: ["Set up React project", "Design UI mockups", "Implement component library", "Set up routing"],
    example: "Frontend skeleton",
    codingTask: "Build frontend foundation: React app with routing, UI component library (shadcn/tailwind), Navigation structure, Authentication pages, Layout components, Responsive design.",
    assignment: "Design and implement: Homepage, Login/Register pages, Dashboard layout, 404 page, Loading states.",
    explanation: "Good UI/UX makes your project shine. Focus on usability and clean design.",
    expectedInputs: "Design requirements",
    expectedOutputs: "Working frontend skeleton",
    evaluationChecklist: ["React app running", "Routing works", "Components reusable", "Design clean"],
    gitTask: "Commit frontend foundation",
    resourceLinks: [],
    tasks: [
      { id: "d111_handson", label: "Complete hands-on: Frontend setup" },
      { id: "d111_coding", label: "Coding task: Frontend foundation" },
      { id: "d111_assignment", label: "Assignment: Core pages implementation" },
      { id: "d111_git", label: "Git: Commit frontend foundation" }
    ]
  },
  {
    day: 112,
    month: 6,
    week: 23,
    monthTitle: "Capstone Project",
    weekTitle: "Frontend & Full Stack",
    topic: "Frontend-Backend Integration",
    handsOn: ["Connect to API", "Implement authentication", "Handle API calls", "Manage state"],
    example: "Full-stack connectivity",
    codingTask: "Connect frontend to backend: Set up axios/fetch, Implement authentication flow, Store JWT tokens, Create API service layer, Handle loading and errors, Add protected routes.",
    assignment: "Implement API integration for: User authentication, Main features (3-5 screens), Real-time updates (if applicable), Error handling, Success notifications.",
    explanation: "This is where frontend and backend come together. Ensure smooth communication.",
    expectedInputs: "User interactions",
    expectedOutputs: "Integrated full-stack app",
    evaluationChecklist: ["API calls work", "Authentication functional", "State managed correctly", "Errors handled"],
    gitTask: "Commit frontend-backend integration",
    resourceLinks: [],
    tasks: [
      { id: "d112_handson", label: "Complete hands-on: API integration" },
      { id: "d112_coding", label: "Coding task: Authentication and API layer" },
      { id: "d112_assignment", label: "Assignment: Core features integration" },
      { id: "d112_git", label: "Git: Commit integration" }
    ]
  },
  {
    day: 113,
    month: 6,
    week: 23,
    monthTitle: "Capstone Project",
    weekTitle: "Frontend & Full Stack",
    topic: "Core Feature UI Implementation",
    handsOn: ["Build main feature screens", "Implement user interactions", "Add data visualization", "Polish UX"],
    example: "Complete user experience",
    codingTask: "Implement main features UI: 3-5 core feature screens, Interactive components, Data tables/lists, Forms with validation, Real-time feedback.",
    assignment: "Add polish and UX improvements: Loading skeletons, Empty states, Error messages, Success feedback, Keyboard shortcuts, Tooltips and help text.",
    explanation: "This is what users will interact with. Make it intuitive and delightful.",
    expectedInputs: "User actions",
    expectedOutputs: "Polished feature screens",
    evaluationChecklist: ["All features accessible via UI", "Interactions smooth", "Feedback clear", "UX polished"],
    gitTask: "Commit core feature UI",
    resourceLinks: [],
    tasks: [
      { id: "d113_handson", label: "Complete hands-on: Feature screen development" },
      { id: "d113_coding", label: "Coding task: Core feature UI" },
      { id: "d113_assignment", label: "Assignment: UX polish and feedback" },
      { id: "d113_git", label: "Git: Commit feature UI" }
    ]
  },
  {
    day: 114,
    month: 6,
    week: 23,
    monthTitle: "Capstone Project",
    weekTitle: "Frontend & Full Stack",
    topic: "Advanced Frontend Features",
    handsOn: ["Add data visualization", "Implement search/filter", "Add export functionality", "Optimize performance"],
    example: "Feature-rich frontend",
    codingTask: "Add advanced frontend features: Charts/graphs (if applicable), Advanced search and filtering, Data export (CSV/PDF), Drag-and-drop (if needed), Real-time updates.",
    assignment: "Optimize frontend performance: Code splitting, Lazy loading, Image optimization, Reduce bundle size, Lighthouse score 80+.",
    explanation: "Advanced features differentiate your project. Optimize for performance and user experience.",
    expectedInputs: "Complex user workflows",
    expectedOutputs: "Fast, feature-rich interface",
    evaluationChecklist: ["Advanced features work", "Performance optimized", "No console errors", "Mobile responsive"],
    gitTask: "Commit advanced features and optimizations",
    resourceLinks: [],
    tasks: [
      { id: "d114_handson", label: "Complete hands-on: Advanced features" },
      { id: "d114_coding", label: "Coding task: Data visualization and advanced UI" },
      { id: "d114_assignment", label: "Assignment: Performance optimization" },
      { id: "d114_git", label: "Git: Commit advanced features" }
    ]
  },
  {
    day: 115,
    month: 6,
    week: 23,
    monthTitle: "Capstone Project",
    weekTitle: "Frontend & Full Stack",
    topic: "End-to-End Testing & Bug Fixes",
    handsOn: ["Manual testing", "Fix bugs", "Cross-browser testing", "Mobile testing"],
    example: "Production-ready app",
    codingTask: "Comprehensive testing: Test all user flows, Test edge cases, Cross-browser testing, Mobile responsiveness, Fix all critical bugs.",
    assignment: "Create test documentation: Test cases (50+), Known issues list, Browser compatibility matrix, Mobile device testing results, Bug fix log.",
    explanation: "Thorough testing ensures quality. Find and fix bugs before users do.",
    expectedInputs: "All possible user scenarios",
    expectedOutputs: "Stable, tested application",
    evaluationChecklist: ["All flows tested", "Critical bugs fixed", "Works on multiple browsers", "Mobile functional"],
    gitTask: "Commit bug fixes and test documentation",
    resourceLinks: [],
    tasks: [
      { id: "d115_handson", label: "Complete hands-on: Comprehensive testing" },
      { id: "d115_coding", label: "Coding task: Bug fixes" },
      { id: "d115_assignment", label: "Assignment: Test documentation" },
      { id: "d115_git", label: "Git: Commit fixes and tests" }
    ]
  },
  {
    day: 116,
    month: 6,
    week: 24,
    monthTitle: "Capstone Project",
    weekTitle: "Deployment & Finalization",
    topic: "Containerization & Deployment Preparation",
    handsOn: ["Create Dockerfiles", "Docker compose setup", "Environment configuration", "Build pipeline"],
    example: "Deployment-ready containers",
    codingTask: "Prepare for deployment: Create Dockerfile for backend, Create Dockerfile for frontend, Docker compose for local development, Environment variable management, Build and test containers locally.",
    assignment: "Set up deployment infrastructure: Choose cloud provider (AWS/GCP/Vercel/Railway), Set up database (MongoDB Atlas, PostgreSQL), Configure secrets management, Set up domain (optional), SSL certificates.",
    explanation: "Containerization ensures consistent deployment across environments.",
    expectedInputs: "Application code",
    expectedOutputs: "Docker containers ready for deployment",
    evaluationChecklist: ["Containers build successfully", "App runs in containers", "Environment vars configured", "Cloud setup complete"],
    gitTask: "Commit Docker configuration",
    resourceLinks: [],
    tasks: [
      { id: "d116_handson", label: "Complete hands-on: Containerization" },
      { id: "d116_coding", label: "Coding task: Docker setup" },
      { id: "d116_assignment", label: "Assignment: Cloud infrastructure setup" },
      { id: "d116_git", label: "Git: Commit Docker configs" }
    ]
  },
  {
    day: 117,
    month: 6,
    week: 24,
    monthTitle: "Capstone Project",
    weekTitle: "Deployment & Finalization",
    topic: "Cloud Deployment & CI/CD",
    handsOn: ["Deploy to cloud", "Set up CI/CD", "Configure monitoring", "Load testing"],
    example: "Live production application",
    codingTask: "Deploy application: Deploy backend to cloud, Deploy frontend to cloud, Connect to production database, Set up CI/CD pipeline, Configure auto-deployment.",
    assignment: "Production setup: Set up monitoring and logging, Configure alerts, Set up backup strategy, Load test application, Create incident response plan.",
    explanation: "Live deployment is the ultimate validation. Ensure reliability and monitoring.",
    expectedInputs: "Production traffic",
    expectedOutputs: "Stable cloud deployment",
    evaluationChecklist: ["Application live and accessible", "CI/CD working", "Monitoring in place", "Performance acceptable under load"],
    gitTask: "Commit deployment scripts and configs",
    resourceLinks: [],
    tasks: [
      { id: "d117_handson", label: "Complete hands-on: Cloud deployment" },
      { id: "d117_coding", label: "Coding task: CI/CD pipeline" },
      { id: "d117_assignment", label: "Assignment: Production infrastructure" },
      { id: "d117_git", label: "Git: Commit deployment configs" }
    ]
  },
  {
    day: 118,
    month: 6,
    week: 24,
    monthTitle: "Capstone Project",
    weekTitle: "Deployment & Finalization",
    topic: "Documentation & Code Quality",
    handsOn: ["Write comprehensive README", "API documentation", "Code comments", "User guide"],
    example: "Professional documentation",
    codingTask: "Create documentation: Comprehensive README with setup instructions, API documentation (Swagger/Postman), Architecture documentation, Database schema documentation, Deployment guide.",
    assignment: "Code quality improvements: Add code comments, Clean up unused code, Consistent formatting, Update all dependencies, Run security audit, Add LICENSE file.",
    explanation: "Documentation is crucial for portfolio projects. It shows professionalism and helps others understand your work.",
    expectedInputs: "Project code and architecture",
    expectedOutputs: "Complete documentation suite",
    evaluationChecklist: ["README comprehensive", "API documented", "Code commented", "Setup instructions clear"],
    gitTask: "Commit all documentation",
    resourceLinks: [],
    tasks: [
      { id: "d118_handson", label: "Complete hands-on: Documentation writing" },
      { id: "d118_coding", label: "Coding task: Complete documentation suite" },
      { id: "d118_assignment", label: "Assignment: Code quality improvements" },
      { id: "d118_git", label: "Git: Commit documentation" }
    ]
  },
  {
    day: 119,
    month: 6,
    week: 24,
    monthTitle: "Capstone Project",
    weekTitle: "Deployment & Finalization",
    topic: "Performance Optimization & Security Audit",
    handsOn: ["Performance profiling", "Security scanning", "Optimization", "Final testing"],
    example: "Production-grade application",
    codingTask: "Final optimizations: Profile and optimize slow endpoints, Optimize database queries, Reduce API response times, Optimize frontend bundle size, Fix security vulnerabilities.",
    assignment: "Security audit: Run security scanners (Snyk, OWASP), Fix all high/critical vulnerabilities, Add rate limiting, Implement CORS properly, Add input sanitization, Update security headers.",
    explanation: "Final polish ensures your application is production-grade and secure.",
    expectedInputs: "Security and performance tests",
    expectedOutputs: "Optimized, secure application",
    evaluationChecklist: ["Performance optimized", "No critical security issues", "All tests passing", "Ready for production"],
    gitTask: "Commit final optimizations",
    resourceLinks: [],
    tasks: [
      { id: "d119_handson", label: "Complete hands-on: Performance profiling" },
      { id: "d119_coding", label: "Coding task: Optimization implementation" },
      { id: "d119_assignment", label: "Assignment: Security audit and fixes" },
      { id: "d119_git", label: "Git: Commit optimizations" }
    ]
  },
  {
    day: 120,
    month: 6,
    week: 24,
    monthTitle: "Capstone Project",
    weekTitle: "Deployment & Finalization",
    topic: "Final Presentation & Portfolio Integration",
    handsOn: ["Create demo video", "Prepare presentation", "Update portfolio", "Final showcase"],
    example: "Capstone presentation",
    codingTask: "Final deliverables: Record demo video (5-10 minutes), Create presentation slides, Write project blog post, Add to portfolio website, Prepare project defense Q&A.",
    assignment: "Reflection and next steps: Write project retrospective (what went well, what to improve), Create 'Lessons Learned' document, Plan future enhancements, Update resume with project, Share on LinkedIn/Twitter.",
    explanation: "Strong presentation skills are crucial. Showcase your hard work effectively to potential employers.",
    expectedInputs: "Complete project",
    expectedOutputs: "Professional presentation materials",
    evaluationChecklist: ["Demo video professional", "Presentation comprehensive", "Portfolio updated", "All deliverables complete"],
    gitTask: "Final commit, Create release v1.0, Tag capstone-complete, Add project to portfolio",
    resourceLinks: [
      { title: "Project Presentation Guide", url: "https://youtu.be/4VZkvrFyxRo?si=mQzVbXrYtN9pWKAb" }
    ],
    tasks: [
      { id: "d120_resources", label: "Review learning resources" },
      { id: "d120_handson", label: "Complete hands-on: Presentation materials" },
      { id: "d120_coding", label: "Coding task: Final deliverables" },
      { id: "d120_assignment", label: "Assignment: Reflection and portfolio update" },
      { id: "d120_git", label: "Git: Final release v1.0, tag capstone-complete" }
    ]
  }
];